/**
 * UX Analytics Events - Type-Safe Event Taxonomy
 * 
 * TASK-006: Centralized UX event definitions with type-safe payloads
 * 
 * This module provides:
 * 1. Canonical event names (UX_EVENTS constant)
 * 2. Type-safe payload interfaces for each event
 * 3. Type guards and validation
 * 
 * Usage:
 * ```ts
 * import { trackUX, UX_EVENTS } from '@/lib/analytics/ux-events';
 * 
 * trackUX(UX_EVENTS.AUTH_MAGIC_LINK_REQUESTED, {
 *   surface: 'public',
 *   device_type: 'mobile'
 * });
 * ```
 * 
 * @see PRD-USER-001 Appendix D (Analytics Events)
 * @see TASK-006 UX Analytics & Metrics Baseline
 */

import { track as baseTrack } from './track';
import { getSessionState } from './session-state';
import { recordUserAction } from './journey-buffer';
import { logAppEvent } from '@/lib/telemetry/logAppEvent';

/**
 * Canonical UX event names
 * 
 * All user-facing analytics events must use these constants.
 * Direct string literals are forbidden in user components.
 */
export const UX_EVENTS = {
  // Auth
  AUTH_MAGIC_LINK_REQUESTED: 'auth_magic_link_requested',
  AUTH_MAGIC_LINK_FAILED: 'auth_magic_link_failed',
  AUTH_MAGIC_LINK_RATE_LIMITED: 'auth_magic_link_rate_limited',

  // Onboarding
  ONBOARDING_STARTED: 'onboarding_started',
  ONBOARDING_STEP_VIEWED: 'onboarding_step_viewed',
  ONBOARDING_STEP_COMPLETED: 'onboarding_step_completed',
  ONBOARDING_PATH_SELECTED: 'onboarding_path_selected',
  ONBOARDING_COMPLETED: 'onboarding_completed',
  SETUP_VIEWED: 'setup_viewed',
  SETUP_ACTION_CLICKED: 'setup_action_clicked',

  // KYC
  KYC_STARTED: 'kyc_started',
  KYC_STATUS_CHANGED: 'kyc_status_changed',
  BRIDGE_TOS_OPENED: 'bridge_tos_opened',

  // Dashboard
  DASHBOARD_VIEWED: 'dashboard_viewed',
  DASHBOARD_ONBOARDING_VIEWED: 'dashboard_onboarding_viewed',
  DASHBOARD_ONBOARDING_WALLET_CREATE_CLICKED: 'dashboard_onboarding_wallet_create_clicked',
  DASHBOARD_ONBOARDING_LIQUIDATION_ADDRESS_CLICKED: 'dashboard_onboarding_liquidation_address_clicked',
  DASHBOARD_ONBOARDING_VIRTUAL_ACCOUNT_CLICKED: 'dashboard_onboarding_virtual_account_clicked',
  DASHBOARD_ONBOARDING_DISMISSED: 'dashboard_onboarding_dismissed',

  // Wallet
  WALLET_OPENED: 'wallet_opened',
  WALLET_CREATED: 'wallet_created',
  WALLET_TRANSFER_QUOTE_REQUESTED: 'wallet_transfer_quote_requested',
  WALLET_TRANSFER_CONFIRMED: 'wallet_transfer_confirmed',

  // Transactions
  TRANSACTION_HISTORY_VIEWED: 'transaction_history_viewed',
  TRANSACTION_EXPORTED: 'transaction_exported',

  // Mobile
  MOBILE_NAVIGATION_CLICK: 'mobile_navigation_click',

  // Address Management
  ADDRESS_CREATED: 'address_created',
  ADDRESS_COPIED: 'address_copied',

  // Errors
  ERROR_SHOWN: 'error_shown',
  ERROR_RETRY_CLICKED: 'error_retry_clicked',
} as const;

/**
 * Type for all valid UX event names
 */
export type UXEventName = (typeof UX_EVENTS)[keyof typeof UX_EVENTS];

/**
 * Type-safe payload definitions for each event
 * 
 * Each event has a specific payload structure that is enforced at compile time.
 */
export interface UxEventPayloads {
  // Auth events
  auth_magic_link_requested: {
    surface: 'public' | 'admin';
    device_type: 'mobile' | 'desktop' | 'telegram' | 'unknown';
  };
  auth_magic_link_failed: {
    surface: 'public' | 'admin';
    device_type: 'mobile' | 'desktop' | 'telegram' | 'unknown';
    error_type?: string;
  };
  auth_magic_link_rate_limited: {
    surface: 'public' | 'admin';
    device_type: 'mobile' | 'desktop' | 'telegram' | 'unknown';
    retry_after_ms?: number;
  };

  // Onboarding events
  onboarding_started: {
    source: 'landing' | 'referral' | 'direct';
  };
  onboarding_step_viewed: {
    step: number;
    stage: string;
  };
  onboarding_step_completed: {
    step: number;
    event: string;
    duration_ms?: number;
  };
  onboarding_path_selected: {
    path: 'full_setup' | 'receive_only';
    mode: 'full' | 'receive_only';
  };
  onboarding_completed: {
    total_duration_ms: number;
    path: 'full' | 'receive_only';
  };
  setup_viewed: Record<string, never>;
  setup_action_clicked: {
    action: 'create_wallet' | 'skip';
  };

  // KYC events
  kyc_started: {
    source: 'wizard_step_2' | 'retry';
  };
  kyc_status_changed: {
    from: string;
    to: string;
  };
  bridge_tos_opened: {
    source: 'wizard_step_2' | 'wizard_step_2_inline' | 'wizard_step_2_fallback';
  };

  // Dashboard events
  dashboard_viewed: {
    entry: 'post_onboarding' | 'nav';
  };
  dashboard_onboarding_viewed: {
    is_first_time: boolean;
    has_wallet: boolean;
  };
  dashboard_onboarding_wallet_create_clicked: {
    source: 'onboarding_screen';
  };
  dashboard_onboarding_liquidation_address_clicked: {
    source: 'onboarding_screen';
  };
  dashboard_onboarding_virtual_account_clicked: {
    source: 'onboarding_screen';
  };
  dashboard_onboarding_dismissed: {
    wallet_created: boolean;
  };

  // Wallet events
  wallet_opened: {
    source: 'nav' | 'dashboard';
  };
  wallet_created: {
    chain: string;
    asset: string;
    source: 'onboarding' | 'manual';
  };
  wallet_transfer_quote_requested: {
    currency: string;
    amount: number;
  };
  wallet_transfer_confirmed: {
    currency: string;
    amount: number;
    status: string;
  };

  // Transaction events
  transaction_history_viewed: {
    source: 'nav' | 'dashboard';
    has_filters: boolean;
  };
  transaction_exported: {
    count: number;
  };

  // Mobile events
  mobile_navigation_click: {
    tab: string;
    from_path: string;
    to_path: string;
  };

  // Address events
  address_created: {
    asset: string;
    network: string;
    source: 'setup_step' | 'dashboard' | 'address_page';
  };
  address_copied: {
    asset: string;
    network: string;
    source: 'dashboard' | 'address_page' | 'receive';
  };

  // Error events
  error_shown: {
    type: string;
    surface: string;
    critical?: boolean;
  };
  error_retry_clicked: {
    type: string;
    surface: string;
  };
}

/**
 * Extract payload type for a specific event
 */
export type UxEventPayload<E extends UXEventName> = UxEventPayloads[E];

/**
 * Type guard to validate event payload structure
 * 
 * Useful for runtime validation in development
 */
export function isValidUxEventPayload<E extends UXEventName>(
  event: E,
  payload: unknown
): payload is UxEventPayload<E> {
  // Basic structure validation
  if (!payload || typeof payload !== 'object') {
    return false;
  }

  // Event-specific validation can be added here
  // For now, we rely on TypeScript compile-time checks
  return true;
}

/**
 * Type-safe UX event tracking function
 * 
 * Wraps the base `track()` function with type safety for UX events.
 * Ensures that only valid event names and payloads can be tracked.
 * 
 * @example
 * ```ts
 * trackUX(UX_EVENTS.AUTH_MAGIC_LINK_REQUESTED, {
 *   surface: 'public',
 *   device_type: 'mobile'
 * });
 * ```
 * 
 * @param event - Canonical UX event name from UX_EVENTS
 * @param payload - Type-safe payload matching the event's payload interface
 */
export function trackUX<E extends UXEventName>(
  event: E,
  payload: UxEventPayload<E>
): void {
  // Runtime validation in development
  if (import.meta.env.DEV) {
    if (!isValidUxEventPayload(event, payload)) {
      console.warn(`[trackUX] Invalid payload for event ${event}:`, payload);
    }
  }

  // Check if journey tracking is enabled
  const journeyTrackingEnabled = import.meta.env.VITE_ENABLE_JOURNEY_TRACKING === 'true';

  // Get session context if journey tracking enabled
  const sessionState = journeyTrackingEnabled ? getSessionState() : null;

  // Enrich payload with common UX context + session tracking
  const enrichedPayload = {
    ...payload,
    // Add environment context
    env: import.meta.env.MODE || 'unknown',
    // Add timestamp
    timestamp: Date.now(),
    // Add session context if enabled
    ...(sessionState && {
      sessionId: sessionState.session_id,
      requestId: sessionState.request_id,
    }),
  };

  // Record to journey buffer if enabled
  if (journeyTrackingEnabled && sessionState) {
    recordUserAction(event, enrichedPayload);
  }

  // Call the base track function (gtag)
  baseTrack(event, enrichedPayload);

  // Persist to database via RPC if journey tracking enabled
  if (journeyTrackingEnabled && sessionState) {
    logAppEvent({
      eventType: `ux.${event}`,
      actorType: 'user',
      severity: 'info',
      source: 'frontend',
      payload: enrichedPayload,
      sessionId: sessionState.session_id,
      requestId: sessionState.request_id,
    }).catch((error) => {
      // Silent fail to not disrupt UX
      if (import.meta.env.DEV) {
        console.warn('[trackUX] Failed to persist event:', error);
      }
    });
  }
}

